import type { BeatAnalysis } from '../types';

/**
 * Beat Detector using Web Audio API
 */
export class BeatDetector {
    private audioContext: AudioContext | null = null;
    private audioBuffer: AudioBuffer | null = null;
    private beats: number[] = [];
    private bpm: number = 0;

    /**
     * Analyze audio file for beats
     */
    async analyzeBeats(audioFile: File): Promise<BeatAnalysis> {
        try {
            console.log('BeatDetector: Starting analysis...');

            // Initialize Audio Context
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            console.log('BeatDetector: Audio context created');

            // Read file as array buffer
            const arrayBuffer = await audioFile.arrayBuffer();
            console.log('BeatDetector: File read, size:', arrayBuffer.byteLength);

            // Decode audio data
            console.log('BeatDetector: Decoding audio...');
            this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            console.log('BeatDetector: Audio decoded, duration:', this.audioBuffer.duration);

            // Extract audio data
            const channelData = this.audioBuffer.getChannelData(0);
            const sampleRate = this.audioBuffer.sampleRate;
            const duration = this.audioBuffer.duration;

            // Detect beats using spectral flux algorithm
            console.log('BeatDetector: Detecting beats...');
            this.beats = this.detectBeatsSpectralFlux(channelData, sampleRate);
            console.log('BeatDetector: Found', this.beats.length, 'beats');

            // Calculate BPM
            this.bpm = this.calculateBPM(this.beats, duration);
            console.log('BeatDetector: BPM calculated:', this.bpm);

            return {
                beats: this.beats,
                bpm: this.bpm,
                duration: duration,
                sampleRate: sampleRate
            };
        } catch (error) {
            console.error('Beat detection error:', error);
            throw error;
        }
    }

    /**
     * Detect beats using spectral flux algorithm
     */
    private detectBeatsSpectralFlux(channelData: Float32Array, sampleRate: number): number[] {
        const beats: number[] = [];
        const windowSize = 1024;
        const hopSize = 512;
        const threshold = 1.5;

        // Calculate energy for each window
        const energies: number[] = [];
        for (let i = 0; i < channelData.length - windowSize; i += hopSize) {
            let energy = 0;
            for (let j = 0; j < windowSize; j++) {
                energy += channelData[i + j] * channelData[i + j];
            }
            energies.push(energy / windowSize);
        }

        // Calculate spectral flux (difference between consecutive energies)
        const fluxes: number[] = [];
        for (let i = 1; i < energies.length; i++) {
            const flux = Math.max(0, energies[i] - energies[i - 1]);
            fluxes.push(flux);
        }

        // Calculate adaptive threshold
        const windowLength = 10;
        for (let i = 0; i < fluxes.length; i++) {
            const start = Math.max(0, i - windowLength);
            const end = Math.min(fluxes.length, i + windowLength);

            let sum = 0;
            for (let j = start; j < end; j++) {
                sum += fluxes[j];
            }
            const average = sum / (end - start);

            // Detect beat if flux exceeds threshold * average
            if (fluxes[i] > threshold * average && fluxes[i] > 0.01) {
                const timestamp = (i * hopSize) / sampleRate;

                // Avoid beats too close together (minimum 0.2 seconds apart)
                if (beats.length === 0 || timestamp - beats[beats.length - 1] > 0.2) {
                    beats.push(timestamp);
                }
            }
        }

        return beats;
    }

    /**
     * Calculate BPM from detected beats
     */
    private calculateBPM(beats: number[], _duration: number): number {
        if (beats.length < 2) return 0;

        // Calculate intervals between beats
        const intervals: number[] = [];
        for (let i = 1; i < beats.length; i++) {
            intervals.push(beats[i] - beats[i - 1]);
        }

        // Calculate median interval (more robust than average)
        intervals.sort((a, b) => a - b);
        const medianInterval = intervals[Math.floor(intervals.length / 2)];

        // Convert to BPM
        const bpm = 60 / medianInterval;

        return Math.round(bpm);
    }

    /**
     * Get beat timestamps for video transitions
     */
    getTransitionTimestamps(videoDuration: number, clipCount: number): number[] {
        if (this.beats.length === 0 || clipCount <= 1) {
            // Fallback: evenly spaced transitions
            const interval = videoDuration / clipCount;
            return Array.from({ length: clipCount - 1 }, (_, i) => (i + 1) * interval);
        }

        // Calculate ideal transition points based on video duration
        const idealInterval = videoDuration / clipCount;
        const transitions: number[] = [];

        for (let i = 1; i < clipCount; i++) {
            const idealTime = i * idealInterval;

            // Find closest beat to ideal time
            let closestBeat = this.beats[0];
            let minDistance = Math.abs(this.beats[0] - idealTime);

            for (const beat of this.beats) {
                const distance = Math.abs(beat - idealTime);
                if (distance < minDistance && beat < videoDuration) {
                    closestBeat = beat;
                    minDistance = distance;
                }
            }

            transitions.push(closestBeat);
        }

        return transitions;
    }

    /**
     * Generate waveform data for visualization
     */
    generateWaveformData(width: number = 1000): number[] {
        if (!this.audioBuffer) return [];

        const channelData = this.audioBuffer.getChannelData(0);
        const samplesPerPixel = Math.floor(channelData.length / width);
        const waveformData: number[] = [];

        for (let i = 0; i < width; i++) {
            const start = i * samplesPerPixel;
            const end = start + samplesPerPixel;

            let max = 0;
            for (let j = start; j < end && j < channelData.length; j++) {
                max = Math.max(max, Math.abs(channelData[j]));
            }

            waveformData.push(max);
        }

        return waveformData;
    }

    /**
     * Clean up resources
     */
    dispose(): void {
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.audioBuffer = null;
        this.beats = [];
        this.bpm = 0;
    }
}
